import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import InputTextarea from "@/components/input-textarea/InputTextarea";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import type { IQuestion } from "@/types/types";
import { useRef, useState, type FC } from "react";
import { uploadImages } from "@/views/inspections/components/inspection/components/answer/utils/upload-images";
import ImageUploader from "@/views/inspections/components/inspection/components/answer/image-uploader/ImageUploader";
import { useQuestionsForm } from "@/store/useQuestionsForm";
import { v4 as uuidv4 } from "uuid";

interface QuestionFormProps {
  item?: IQuestion;
  onClose: () => void;
}

interface IQuestionForm {
  es: string;
  en: string;
  files: File[];
  images: string[];
}

type QuestionFormKeys = keyof IQuestionForm;

const QuestionForm: FC<QuestionFormProps> = ({ item, onClose }) => {
  const refButton = useRef<boolean>(false);
  const { addQuestion, updateQuestion } = useQuestionsForm();
  const [formData, setFormData] = useState<IQuestionForm>({
    es: item?.es ?? "",
    en: item?.en ?? "",
    files: [],
    images: item?.images ?? [],
  });

  const translate = useTranslate();

  const isValid =
    formData.en.trim().length > 0 && formData.es.trim().length > 0;

  const setFiles = (data: File[]) => {
    setFormData((prev) => {
      return { ...prev, files: data };
    });
  };

  const handleOnEdit = (id: QuestionFormKeys, value: any) => {
    setFormData((prev) => {
      const tmp = { ...prev } as any;
      tmp[id] = value;
      return tmp;
    });
  };

  const submitImages = async () => {
    const routeImage = `questions/`;
    const { ok_files, ko_files } = await uploadImages(
      formData.files,
      routeImage,
    );
    return { ok_files, ko_files };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isValid || refButton.current) return;
    refButton.current = true;

    const id = item?.questionId || uuidv4();

    const { ok_files, ko_files } = await submitImages();
    try {
      let allImages = [] as string[];
      if (item && item.images && item.images.length > 0) {
        allImages = [...item.images];
      }

      if (!item) {
        addQuestion({
          questionId: id,
          es: formData.es,
          en: formData.en,
          images: [...allImages, ...ok_files],
        });
      } else {
        updateQuestion(id, {
          questionId: id,
          es: formData.es,
          en: formData.en,
          images: [...allImages, ...ok_files],
        });
      }

      if (ko_files.length > 0) {
        console.error({ ko_files, message: "Ficheros no subidos" });
      }

      onClose();
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <div data-testid="answer-form" className="space-y-4">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-4">
          <InputTextarea
            required
            label={`${translate("questionDescripction")} (${translate(
              "spanish",
            )})`}
            placeholder={`${translate("questionDescripction")} (${translate(
              "spanish",
            )})`}
            value={formData.es}
            onChange={(value) => handleOnEdit("es", value)}
            size={"large"}
            name="es"
          />
          <InputTextarea
            required
            label={`${translate("questionDescripction")} (${translate(
              "english",
            )})`}
            placeholder={`${translate("questionDescripction")} (${translate(
              "english",
            )})`}
            value={formData.en}
            onChange={(value) => handleOnEdit("en", value)}
            size={"large"}
            name="en"
          />
          <ImageUploader
            images={formData.images}
            files={formData.files}
            onChange={setFiles}
            //maxFiles={3}
            buttonText={translate("addImage")}
            cameraButtonText={translate("takePhoto")}
          />
        </div>
        <div className="border my-6" />
        <div className="flex justify-between">
          <div className="flex">
            <SecondaryButton
              label={translate("cancel")}
              colour={"red"}
              onClick={handleOnClose}
            />
          </div>
          <div>
            <PrimaryButton
              type="submit"
              label={translate("saveChanges")}
              colour={"red"}
              disabled={!isValid}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;
